import { ArrowForwardIos, People } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Divider, Grid, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { toast } from 'react-toastify';
import { createInvoiceReminders } from 'src/shared/services/invoiceService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { CreateInvoiceRemindersRequest } from '../models/createInvoiceRemindersRequest';
import { InvoiceReminderDataGrid } from './invoiceReminderDataGrid';
import { InvoiceRemindersSelection } from '../models/invoiceRemindersSelection';
import { SetSendingDate } from './setSendingDate';

type CreateMemberInvoiceReminderProps = {
  invoiceMission: InvoiceMissionResponse;
  openCreateInvoiceReminderModal: boolean;
  setOpenCreateInvoiceReminderModal: (closerModal: boolean) => void;
  reminder: InvoiceSendingTypeIntegerEnum;
};

type CreateMemberInvoiceReminderFormValues = CreateInvoiceRemindersRequest & {
  includeNotSent?: boolean;
};

export const CreateMemberInvoiceReminder = (props: CreateMemberInvoiceReminderProps) => {
  const { invoiceMission, openCreateInvoiceReminderModal, setOpenCreateInvoiceReminderModal, reminder } = props;
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'invoice']);
  const { setDateStamp } = useGlobals();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const { scrollRef } = useScrollIntoView();

  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  const [loading, setLoading] = useState<boolean>(false);
  const [totalInvoices, setTotalInvoices] = useState<number | undefined>(undefined);

  const createMemberInvoiceReminderSchema: yup.Schema<CreateMemberInvoiceReminderFormValues> = yup.object().shape({
    missionId: yup.number().required(),
    templateId: yup.number().required(),
    sendingType: yup.mixed<InvoiceSendingTypeIntegerEnum>().required(),
    async: yup.boolean().required(),
    approvalRequired: yup.boolean().required(),
    sndDate: yup.string().when('approvalRequired', {
      is: true,
      then: (sndDate) => sndDate.required(t('common:validationMessages.required')),
      otherwise: (sndDate) => sndDate.optional(),
    }),
    runAsTaskWithTaskService: yup.boolean().optional(),
    includeNotSent: yup.boolean().optional(),
  });

  const formMethods = useForm<CreateMemberInvoiceReminderFormValues>({
    resolver: yupResolver(createMemberInvoiceReminderSchema),
    mode: 'all',
    defaultValues: {
      missionId: invoiceMission.invoiceMission_id,
      templateId: invoiceMission.template_id,
      sendingType: reminder,
      async: true,
      approvalRequired: isSuperAdmin,
      sndDate: undefined,
      // Påminnelser ska alltid köras via TaskService (in-process async-vägen fallerar tyst).
      // Superadmin kan kryssa ur manuellt; övriga användare ser inte kryssrutan och får detta automatiskt.
      runAsTaskWithTaskService: true,
      includeNotSent: false,
    },
  });

  const { handleSubmit, watch } = formMethods;
  const approvalRequired = watch('approvalRequired');
  const includeNotSent = watch('includeNotSent');

  const searchFilter: InvoiceRemindersSelection = useMemo(
    () => ({
      missionId: invoiceMission.invoiceMission_id,
      sendingType: reminder,
      ...(includeNotSent ? { excludeNotSent: false } : {}),
    }),
    [includeNotSent, invoiceMission.invoiceMission_id, reminder],
  );

  const onSubmit = async (data: CreateMemberInvoiceReminderFormValues) => {
    setLoading(true);

    const { includeNotSent: includeNotSentValue, ...rest } = data;
    const dataToPost: CreateInvoiceRemindersRequest = {
      ...rest,
      sndDate: rest.approvalRequired ? rest.sndDate : undefined,
      ...(includeNotSentValue ? { excludeNotSent: false } : {}),
    };

    try {
      await createInvoiceReminders(dataToPost);
      if (isMounted()) {
        setDateStamp(new Date());
        toast.success(<>{t('invoice:createMemberInvoice.createInvoices')}</>);
        navigate(`${RoutesEnum.InvoiceMissionDetailSendingType}/${data.missionId}/${InvoiceSendingTypeIntegerEnum.NotSet}`);
        setOpenCreateInvoiceReminderModal(false);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:createMemberInvoice.createInvoicesFailed')}</>);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <GenericDialog
        open={openCreateInvoiceReminderModal}
        padding={0}
        maxWidth='md'
        disableBackdropClosing
        dialogTitle={t('invoice:invoiceMissionPage.createReminder', { reminder: reminder === InvoiceSendingTypeIntegerEnum.Main ? 1 : 2 })}
        onClose={() => {
          setOpenCreateInvoiceReminderModal(false);
        }}
        onSave={() => {
          ConsoleHelper.log();
        }}
        dialogActions={
          <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', width: '100%', marginLeft: 1 }}>
            <Button
              type='submit'
              variant='contained'
              form='createInvoiceReminder'
              sx={{ mr: 1 }}
              disabled={loading || totalInvoices === 0}
              disableElevation
              endIcon={<ArrowForwardIos fontSize='small' />}
            >
              {totalInvoices === 1 ? t('invoice:invoiceMissionPage.createInvoice') : t('invoice:invoiceMissionPage.createInvoices')}
            </Button>
          </Box>
        }
        dialogContent={
          <form id='createInvoiceReminder' onSubmit={handleSubmit(onSubmit)}>
            {(totalInvoices ?? 0) > 0 ? (
              <>
                <Alert icon={<People />} sx={{ mt: 1 }} severity='info'>
                  <AlertTitle>
                    {totalInvoices === 1
                      ? t('invoice:invoiceMissionPage.invoiceUnpaid', { count: totalInvoices })
                      : t('invoice:invoiceMissionPage.invoicesUnpaid', { count: totalInvoices })}
                  </AlertTitle>
                </Alert>
                {isSuperAdmin && (
                  <Box ml={2}>
                    <Box mt={1}>
                      <ControlledCheckboxNew
                        name='includeNotSent'
                        customizedLabel={
                          <Typography variant='body2' mt={0.5}>
                            {t('invoice:includeNotSentInvoices')}
                          </Typography>
                        }
                      />
                    </Box>
                    <Box>
                      <ControlledCheckboxNew
                        name='runAsTaskWithTaskService'
                        customizedLabel={
                          <Typography variant='body2' mt={0.5}>
                            {t('invoice:runAsTask')}
                          </Typography>
                        }
                      />
                    </Box>
                    <ControlledCheckboxNew
                      name='approvalRequired'
                      customizedLabel={
                        <Typography variant='body2' mt={0.5}>
                          {t('invoice:createMemberInvoice.mustBeApprovedByCustomer')}
                        </Typography>
                      }
                    />
                    {invoiceMission?.sendingApprovalEmails && (
                      <Typography ml={4} variant='body2' color='textSecondary'>
                        {t('invoice:invoiceMissionPage.approvalNotice', { email: invoiceMission.sendingApprovalEmails })}
                      </Typography>
                    )}
                    <Box ml={4} mt={2} mb={2} sx={{ maxWidth: '300px' }}>
                      <SetSendingDate disabled={!approvalRequired} />
                    </Box>
                    <Divider sx={{ ml: -2 }} />
                  </Box>
                )}
              </>
            ) : (
              <Grid item xs={12}>
                {reminder === InvoiceSendingTypeIntegerEnum.Main && (
                  <Alert severity='info'>{t('invoice:invoiceMissionPage.createReminderAlertInfo', { reminder: 1 })}</Alert>
                )}
              </Grid>
            )}

            <Box
              sx={{
                position: 'relative',
                '& .MuiPaper-root': {
                  boxShadow: 'none',
                },
              }}
              ref={scrollRef}
            >
              <InvoiceReminderDataGrid
                hideCheckBoxSelection
                setTotalInvoices={setTotalInvoices}
                loadingCallback={() => ConsoleHelper.log()}
                searchFilter={searchFilter}
              />
            </Box>
          </form>
        }
      />
    </FormProvider>
  );
};
